<template>
  <div class="flex flex-col gap-8 items-start">
    <h3 v-if="isFreeTier" class="h-[12px]" />
    <h3 class="font-bold">
      {{ headerText }}
    </h3>
    <div
      v-for="(item, index) in featureItems"
      :class="{
        'pb-16': index === featureItems?.length,
      }"
      :key="item.text"
    >
      <div class="flex items-center text-base">
        <component :is="isLockedItem(item) ? BittsTooltip : 'span'">
          <template #title>
            <p>Locked platform feature</p>
          </template>
          <FontAwesomeIcon
            :icon="iconForItem(item)"
            class="h-12 w-12 mr-8"
            :class="
              isCheckoutPage ? 'text-secondary-accent' : 'text-neutral-accent'
            "
          />
        </component>
        <component :is="item.tooltip ? BittsTooltip : 'span'">
          <template #title>
            <p>
              {{ item.tooltip }}
            </p>
          </template>
          <p
            class="text-neutral-text inline-flex items-center gap-4"
            :class="{
              'text-secondary-accent font-bold':
                !isSupernodeTier && isHighlightedItem(item),
            }"
          >
            <span
              v-html="
                item.tooltip
                  ? injectStrings(item.text, {
                      values: [item.injection],
                      bold: false,
                      italic: false,
                      underline: true,
                    })
                  : item.text
              "
            />
            <BittsTag
              v-if="item.isBeta && !(item.isMcpServer && hasMcpMeteringUi)"
              color="beta"
              size="x-small"
              text="Beta"
            />
          </p>
        </component>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { BittsTag, BittsTooltip } from '@crossbeam/bitts';

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';

import { BILLING_IMPROVEMENTS_COPY } from '@/constants/billing';
import type { BillingFeature } from '@/constants/billing';
import { FEATURE_FLAG } from '@/constants/feature_flags';
import { useBillingStore, useFeatureFlagStore } from '@/stores';
import { injectStrings } from '@/utils';

interface Props {
  tier: string;
  comparisonModal?: boolean;
  highlight?: string;
  isCheckoutPage?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  comparisonModal: false,
  highlight: '',
  isCheckoutPage: false,
});

const { hasFeatureFlag } = useFeatureFlagStore();
const { isLegacyConnectorPPv5 } = storeToRefs(useBillingStore());
const hasNewPricing = computed(() => hasFeatureFlag(FEATURE_FLAG.TEMP_PPV67));
const hasMcpMeteringUi = computed(() =>
  hasFeatureFlag(FEATURE_FLAG.TEMP_MCP_METERING_UI),
);

const headerText = computed(() => {
  if (isFreeTier.value) return;
  let prevTier;
  switch (props.tier) {
    case 'connector':
      prevTier = 'Free';
      break;
    case 'supernode':
      prevTier = 'Connector';
      break;
    default:
      prevTier = 'Supernode';
  }
  return `Everything in ${prevTier}, plus:`;
});

const isSupernodeTier = computed(() => props.tier === 'supernode');
const isFreeTier = computed(() => props.tier === 'explorer');

function replaceReportWithList(str: string | undefined) {
  if (!str) return str;
  return str.replace(/\breports\b/gi, 'Lists');
}

const featureItems = computed(() => {
  return BILLING_IMPROVEMENTS_COPY[props.tier]?.features_v5
    ?.filter((item) =>
      hasNewPricing.value
        ? !item.hiddenUnderNewPricing
        : !item.newPricingOnly && !item.isPlatformFeature,
    )
    .map((item) => {
      const withOverrides = hasNewPricing.value
        ? {
            ...item,
            text: item.textNewPricing ?? item.text,
            tooltip: item.tooltipNewPricing ?? item.tooltip,
            injection: item.injectionNewPricing ?? item.injection,
          }
        : item;
      const tooltip = replaceReportWithList(withOverrides.tooltip);
      return {
        ...withOverrides,
        text: replaceReportWithList(withOverrides.text) ?? withOverrides.text,
        tooltip:
          hasMcpMeteringUi.value && item.tooltipCreditSuffix && tooltip
            ? `${tooltip}${item.tooltipCreditSuffix}`
            : tooltip,
        injection: replaceReportWithList(withOverrides.injection),
      };
    });
});

function iconForItem(item: BillingFeature) {
  if (item.isPlatformFeature && isLegacyConnectorPPv5.value)
    return ['fak', 'lock'];
  if (props.comparisonModal) return ['far', 'check'];
  return ['fak', `${item.icon}`];
}

function isLockedItem(item: BillingFeature) {
  return !!item.isPlatformFeature && isLegacyConnectorPPv5.value;
}

function isHighlightedItem(item: BillingFeature) {
  return item.cta?.includes(props.highlight);
}
</script>
